/**
 * Vorlage „Ablöschen“.
 *
 * In der heißen Pfanne liegt Angebratenes, am Boden haftet dunkler
 * Bratensatz. Wein, Brühe oder Wasser wird angegossen, es zischt und dampft
 * kräftig. Dann löst der Kochlöffel den Bratensatz vom Boden.
 */
import { ease, segment, wave } from '../geometry.ts';
import { STROKE } from '../palette.ts';
import type { Tone } from '../palette.ts';
import { circle, compact, ellipse, faded, group } from '../scene.ts';
import { sizzle, steam } from '../parts/effects.ts';
import { meatPiece } from '../parts/food.ts';
import { pourer, stream } from '../parts/kitchen.ts';
import { arrow, counter } from '../parts/stage.ts';
import { GRATE_Y, stove, stoveKnob } from '../parts/stove.ts';
import { place, woodenSpoon } from '../parts/utensils.ts';
import { pan } from '../parts/vessels.ts';
import { choice, defineTemplate, textValue } from '../template.ts';

const PAN = { cx: 92, width: 124 } as const;
const RIM_Y = GRATE_Y - 13 - PAN.width * 0.1;
const FLOOR_Y = RIM_Y + 6;
const KNOB_X = 214;
const POWER = 0.85;
/** Ausgussecke des Bechers. */
const LIP = { x: 150, y: 40 } as const;

/** Zeitplan. */
const TIME = {
  cupIn: [0.04, 0.1],
  tilt: [0.1, 0.26],
  untilt: [0.48, 0.58],
  cupOut: [0.56, 0.64],
  flowIn: [0.24, 0.28],
  flowOut: [0.46, 0.5],
  pool: [0.26, 0.5],
  burst: [0.26, 0.32],
  calm: [0.38, 0.75],
  scrape: [0.6, 0.86],
  reset: [0.9, 1],
} as const;

/** Flüssigkeiten und ihr Ton. */
const LIQUIDS = { wine: 'Wein', broth: 'Brühe', water: 'Wasser' } as const;
const LIQUID_TONES: Readonly<Record<keyof typeof LIQUIDS, Tone>> = {
  wine: 'deep',
  broth: 'shade',
  water: 'tint',
};

/** Lage des Bratensatzes am Pfannenboden. */
const SPOTS = [
  [-44, 4],
  [-22, 9],
  [4, 8],
  [24, 3],
  [40, 8],
  [14, -6],
  [-30, -5],
] as const;

/** Bratensatz: kleine dunkle Flecken ohne Kontur. */
const SPOT_STYLE = { fill: 'deep', stroke: 'none' } as const;

/** Vorlage „Ablöschen“. */
export const stoveDeglazeTemplate = defineTemplate({
  id: 'stove.deglaze',
  group: 'stove',
  title: 'Ablöschen',
  description:
    'Flüssigkeit wird in die heiße Pfanne gegossen. Es zischt und dampft, und der Bratensatz am Boden wird mit dem Kochlöffel gelöst.',
  durationSeconds: 7,
  keywords: [
    'ablöschen',
    'abloeschen',
    'mit wein',
    'mit brühe',
    'aufgießen',
    'angießen',
    'bratensatz',
    'loskochen',
  ],
  stillTime: 0.36,
  parameters: [choice('liquid', 'Flüssigkeit', LIQUIDS, 'wine')],
  inferValues(text) {
    const lower = text.toLowerCase();
    if (lower.includes('wein') || lower.includes('portwein')) return { liquid: 'wine' };
    if (lower.includes('brühe') || lower.includes('fond')) return { liquid: 'broth' };
    if (lower.includes('wasser')) return { liquid: 'water' };
    return {};
  },
  scene(values, t) {
    const tone = LIQUID_TONES[textValue(values, 'liquid', 'wine') as keyof typeof LIQUIDS];
    const vessel = pan(PAN.cx, RIM_Y, PAN.width);
    const burner = stove(PAN.cx, POWER, t);
    const reset = ease.inOutSine(segment(t, ...TIME.reset));
    const pool = ease.outQuad(segment(t, ...TIME.pool)) * (1 - reset);
    const flow =
      ease.inOutSine(segment(t, ...TIME.flowIn)) * (1 - ease.inOutSine(segment(t, ...TIME.flowOut)));
    const burst = ease.outQuad(segment(t, ...TIME.burst)) * (1 - ease.inOutSine(segment(t, ...TIME.calm)));
    const scrape = segment(t, ...TIME.scrape);
    const spots = Math.min(1, 1 - ease.inOutSine(scrape) + reset);
    const tilt =
      -78 * (ease.inOutCubic(segment(t, ...TIME.tilt)) - ease.inOutCubic(segment(t, ...TIME.untilt)));
    const cup = ease.outCubic(segment(t, ...TIME.cupIn)) * (1 - segment(t, ...TIME.cupOut));
    const fullness = 0.75 - 0.6 * segment(t, TIME.flowIn[0], TIME.flowOut[1]);
    const spoonOn = Math.sin(Math.PI * scrape);

    const rx = (PAN.width / 2) * 0.78;
    return compact([
      ...counter(),
      ...burner.back,
      ...vessel.back,
      faded(
        spots,
        SPOTS.map(([dx, dy]) => circle(PAN.cx + dx, FLOOR_Y + dy, 2.6, SPOT_STYLE)),
      ),
      pool > 0.01
        ? group(
            [
              ellipse(PAN.cx + 4, FLOOR_Y + 3, rx * pool, 15 * pool, {
                fill: tone,
                stroke: 'ink',
                strokeWidth: STROKE.detail,
              }),
            ],
            {
              clip: vessel.opening,
            },
          )
        : null,
      meatPiece(PAN.cx - 30, FLOOR_Y - 9, 22, 14, 0.6, 0.6),
      meatPiece(PAN.cx - 6, FLOOR_Y - 13, 22, 14, 0.6, 0.6),
      meatPiece(PAN.cx + 20, FLOOR_Y - 8, 22, 14, 0.6, 0.6),
      faded(spoonOn * 1.4, [place(PAN.cx + 26 + wave(t, 6) * 12, FLOOR_Y + 6, 30, woodenSpoon())]),
      ...vessel.front,
      ...burner.front,
      flow > 0.02
        ? stream(
            [LIP, { x: LIP.x - 4, y: LIP.y + 26 }, { x: PAN.cx + 36, y: FLOOR_Y + 2 }],
            7 * flow,
            t,
            tone,
          )
        : null,
      faded(cup, [pourer(LIP, 26, 34, tilt, fullness, tone)]),
      ...sizzle(PAN.cx, FLOOR_Y - 4, 46, 0.25 + burst * 0.75, t, 8),
      ...steam(PAN.cx, FLOOR_Y - 20, t, { count: 4, spread: 20, height: 34, strength: 0.3 + burst * 0.7 }),
      stoveKnob(KNOB_X, POWER),
    ]);
  },
  hints() {
    return [
      arrow([
        { x: 176, y: 30 },
        { x: 158, y: 56 },
        { x: 136, y: 84 },
      ]),
    ];
  },
});
